/**
 * Service worker registration + PWA install-prompt management.
 *
 * Exposes a small hook so React components can show a custom "تثبيت التطبيق"
 * button when the browser fires `beforeinstallprompt` (Chrome / Edge / Samsung).
 * On iOS Safari this event never fires — we detect iOS and show a helpful
 * "اضغط مشاركة → أضف للشاشة الرئيسية" hint instead.
 */
import { useEffect, useState } from 'react';

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  for (const cb of listeners) {
    try { cb(); } catch { /* swallow */ }
  }
}

// ── Capture the install event before the browser shows its mini-bar ──
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

// ── Detect platform / install state ──────────────────────────────────────
export function isIos() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
}

// ── React hook ───────────────────────────────────────────────────────────
export function useInstallPrompt() {
  const [canInstall, setCanInstall] = useState(!!deferredPrompt);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const update = () => {
      setCanInstall(!!deferredPrompt);
      setInstalled(isStandalone());
    };
    listeners.add(update);
    return () => listeners.delete(update);
  }, []);

  /** Trigger the native install dialog. Returns the user's choice. */
  const promptInstall = async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    deferredPrompt = null;
    notify();
    return result;
  };

  return { canInstall, installed, promptInstall, isIos: isIos() };
}

// ── Service worker registration ──────────────────────────────────────────
// Build id injected by Vite (see vite.config.js `define`). Changes every
// build, so the SW URL `/sw.js?v=<id>` changes too → the browser fetches and
// installs the new worker → we activate it and reload, so users always run
// the latest code without manually clearing the cache.
const BUILD_ID =
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  if (import.meta.env && import.meta.env.DEV) return;

  // When a new SW takes control, reload once so the fresh assets load.
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(`/sw.js?v=${BUILD_ID}`, { scope: '/' })
      .then((reg) => {
        // Check for updates every hour (and on each load via the new URL).
        setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            // A new version finished installing while an old one controls the
            // page → tell it to skip waiting; controllerchange then reloads.
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[pwa] SW registration failed:', err);
      });
  });
}
